import type { DashboardFilters } from '@delivery-metrics/shared';
import {
  CloudOff,
  Assessment,
  Error as ErrorIcon,
  Refresh,
  Speed,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Button,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { FilterPanel, type FilterConfig } from '../components/FilterPanel';
import { CollectorStatsDialog } from '../components/CollectorStatsDialog';
import { EIRnDTopLevelKPISection } from '../components/leadership/EIRnDTopLevelKPISection';
import { useData } from '../contexts/DataContext';
import {
  useDataSources,
  useLeadershipMetrics,
  usePrograms,
  useUnitProgramMap,
  useUnits,
} from '../hooks/useDashboardData';
import { useDashboardFilters } from '../hooks/useDashboardFilters';

type DataSourceOptionsLike = {
  repositories?: string[];
  areaPaths?: string[];
  sonarqubeProjects?: string[];
};

// Case-insensitive prune mirroring the backend filter normalisation; the
// original selection value is preserved in the result.
const pruneToScope = (selected: string[] | undefined, available?: string[]) => {
  const allowed = new Set((available || []).map(value => value.toLowerCase()));
  return (selected || []).filter(value => allowed.has(value.toLowerCase()));
};

const sameStringSet = (a?: string[], b?: string[]) =>
  JSON.stringify((a || []).slice().sort()) ===
  JSON.stringify((b || []).slice().sort());

// Returns filters with data-source selections pruned to the scoped options, or
// the same reference when nothing changed (so callers can skip a state update).
const pruneFilterDataSources = <T extends DashboardFilters>(
  filters: T,
  ds: DataSourceOptionsLike
): T => {
  const repositories = pruneToScope(filters.repositories, ds.repositories);
  const areaPaths = pruneToScope(filters.areaPaths, ds.areaPaths);
  const sonarqubeProjects = pruneToScope(
    filters.sonarqubeProjects,
    ds.sonarqubeProjects
  );
  if (
    repositories.length === (filters.repositories?.length ?? 0) &&
    areaPaths.length === (filters.areaPaths?.length ?? 0) &&
    sonarqubeProjects.length === (filters.sonarqubeProjects?.length ?? 0)
  ) {
    return filters;
  }
  return { ...filters, repositories, areaPaths, sonarqubeProjects };
};

export const EIRnDTopLevelKPIDashboard: React.FC = () => {
  const { filters, updateFilters, resetFilters } = useDashboardFilters();
  const [draftFilters, setDraftFilters] = useState<DashboardFilters>(filters);
  const [isCollectorStatsOpen, setCollectorStatsOpen] = useState(false);
  const { data, isLoading, error, refetch } = useLeadershipMetrics(filters);

  // Sync draft filters when applied filters change (e.g. after reset)
  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const hasPendingChanges =
    JSON.stringify(draftFilters) !== JSON.stringify(filters);
  const programsQuery = usePrograms();
  const unitsQuery = useUnits();
  const unitProgramMapQuery = useUnitProgramMap();
  const dataSourcesQuery = useDataSources(draftFilters);
  const { isRefreshing, hasErrors, clearErrors } = useData();

  // Mock connection status and data freshness for now
  const connectionStatus = {
    isOnline: true,
    isRealTimeConnected: true,
    realTimeError: null,
    hasOfflineData: false,
    isSyncing: false,
  };

  const dataFreshness = {
    lastUpdated: new Date(),
    isStale: false,
    minutesSinceUpdate: 0,
  };

  const refresh = {
    leadership: refetch,
  };

  const unitProgramMap = unitProgramMapQuery.data || {};

  const availablePrograms = useMemo(() => {
    const allPrograms = programsQuery.data || [];
    const selectedUnits = draftFilters.units || [];

    if (!selectedUnits.length) {
      return allPrograms;
    }

    const fromUnits = selectedUnits.flatMap(unit => unitProgramMap[unit] || []);
    const uniquePrograms = Array.from(new Set(fromUnits));
    return uniquePrograms.length ? uniquePrograms : allPrograms;
  }, [draftFilters.units, programsQuery.data, unitProgramMap]);

  const filterOptions = useMemo(() => {
    const ds = dataSourcesQuery.data;
    const toOptions = (values?: string[]) =>
      (values || []).map(value => ({ value, label: value }));
    return {
      units: (unitsQuery.data || []).map(unit => ({
        value: unit,
        label: unit,
      })),
      programs: availablePrograms.map(program => ({
        value: program,
        label: program,
      })),
      repositories: toOptions(ds?.repositories),
      areaPaths: toOptions(ds?.areaPaths),
      sonarqubeProjects: toOptions(ds?.sonarqubeProjects),
    };
  }, [unitsQuery.data, availablePrograms, dataSourcesQuery.data]);

  const applyFilters = (nextFilters: DashboardFilters) => {
    const prevUnits = (filters.units || []).slice().sort();
    const nextUnits = (nextFilters.units || []).slice().sort();
    const unitsChanged =
      JSON.stringify(prevUnits) !== JSON.stringify(nextUnits);

    // Resolve the program scope that will actually be applied (units may
    // auto-select / constrain programs).
    let resolved = nextFilters;
    if (unitsChanged) {
      const scopedPrograms = nextUnits.length
        ? Array.from(
            new Set(nextUnits.flatMap(unit => unitProgramMap[unit] || []))
          )
        : [];
      // Preserve the user's manual program selection when it is a valid
      // subset of the category-scoped programs; only auto-expand to all
      // scoped programs when the user hasn't narrowed the list.
      const userPrograms = (nextFilters.programs || []).filter(p =>
        scopedPrograms.includes(p)
      );
      resolved = {
        ...nextFilters,
        programs: userPrograms.length ? userPrograms : scopedPrograms,
      };
    } else if (
      (nextFilters.units || []).length &&
      (nextFilters.programs || []).length
    ) {
      const allowedPrograms = new Set(
        (nextFilters.units || []).flatMap(unit => unitProgramMap[unit] || [])
      );
      const constrainedPrograms = (nextFilters.programs || []).filter(program =>
        allowedPrograms.has(program)
      );

      if (constrainedPrograms.length !== (nextFilters.programs || []).length) {
        resolved = { ...nextFilters, programs: constrainedPrograms };
      }
    }

    // Still-valid data-source selections must survive Apply; only now-invalid
    // ones are dropped. When the scoped options for the scope being applied are
    // already loaded (they are keyed on draftFilters, and resolving above did
    // not change the program/unit scope), prune synchronously so the KPI
    // queries never run with now-invalid values. Otherwise the prune effect
    // below corrects the applied filters once the freshly scoped options load.
    const ds = dataSourcesQuery.data;
    const optionsMatchResolvedScope =
      !dataSourcesQuery.isFetching &&
      !!ds &&
      sameStringSet(resolved.programs, nextFilters.programs) &&
      sameStringSet(resolved.units, nextFilters.units);

    updateFilters(
      optionsMatchResolvedScope
        ? pruneFilterDataSources(resolved, ds)
        : resolved
    );
  };

  // Data-source filters are per-team drill-downs: only shown once a Product
  // Team (program) is selected, and their options are scoped to that team.
  const hasTeamSelected = (draftFilters.programs?.length ?? 0) > 0;

  // Clear any data-source selections when no team is selected, so hidden
  // filters can never silently narrow the KPIs.
  const handleDraftChange = (next: DashboardFilters) => {
    if (!next.programs?.length) {
      setDraftFilters({
        ...next,
        repositories: [],
        areaPaths: [],
        sonarqubeProjects: [],
      });
      return;
    }
    setDraftFilters(next);
  };

  // Prune data-source selections that are no longer valid for the currently
  // scoped options. When the Product Team / unit scope changes the options
  // refetch; selections belonging to a previously selected team would
  // otherwise linger in draftFilters — invisible in the UI (not in the new
  // options) but still applied to the KPI queries, silently narrowing results.
  // Drop only the values absent from the freshly scoped options so still-valid
  // picks are preserved.
  useEffect(() => {
    const ds = dataSourcesQuery.data;
    if (!ds || dataSourcesQuery.isFetching) {
      return;
    }

    setDraftFilters(prev => pruneFilterDataSources(prev, ds));

    // Also correct the applied filters, but only when the loaded options belong
    // to the applied scope (draft scope === applied scope). Otherwise we could
    // drop selections that are still valid for a different applied scope while
    // the user is editing the draft. This is what removes now-invalid values
    // from the KPI queries after a scope change, while still-valid selections
    // survive Apply.
    const sameScope =
      sameStringSet(draftFilters.programs, filters.programs) &&
      sameStringSet(draftFilters.units, filters.units);
    if (sameScope) {
      const pruned = pruneFilterDataSources(filters, ds);
      if (pruned !== filters) {
        updateFilters({
          repositories: pruned.repositories,
          areaPaths: pruned.areaPaths,
          sonarqubeProjects: pruned.sonarqubeProjects,
        });
      }
    }
  }, [
    dataSourcesQuery.data,
    dataSourcesQuery.isFetching,
    draftFilters.programs,
    draftFilters.units,
    filters,
    updateFilters,
  ]);

  const filterConfigs: FilterConfig[] = [
    {
      key: 'timeRange',
      label: 'Time Period',
      type: 'daterange',
      required: true,
      width: 'large',
    },
    {
      key: 'units',
      label: 'Categories',
      type: 'multiselect',
      options: filterOptions.units,
      width: 'medium',
    },
    {
      key: 'programs',
      label: 'Product Teams',
      type: 'multiselect',
      options: filterOptions.programs,
      width: 'medium',
    },
    ...(hasTeamSelected
      ? ([
          {
            key: 'repositories',
            label: 'Repositories',
            type: 'multiselect',
            options: filterOptions.repositories,
            width: 'medium',
            group: 'dataSources',
          },
          {
            key: 'areaPaths',
            label: 'Areas',
            type: 'multiselect',
            options: filterOptions.areaPaths,
            width: 'medium',
            group: 'dataSources',
          },
          {
            key: 'sonarqubeProjects',
            label: 'SonarQube Projects',
            type: 'multiselect',
            options: filterOptions.sonarqubeProjects,
            width: 'medium',
            group: 'dataSources',
          },
        ] as FilterConfig[])
      : []),
  ];

  // Handle loading state
  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Speed sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant='h4' component='h1'>
            EI Innovation Effectiveness Top Level KPI Dashboard
          </Typography>
          <CircularProgress size={24} sx={{ ml: 2 }} />
        </Box>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Speed sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant='h4' component='h1'>
            EI Innovation Effectiveness Top Level KPI Dashboard
          </Typography>
        </Box>

        <Alert
          severity='error'
          action={
            <IconButton color='inherit' size='small' onClick={() => refetch()}>
              <Refresh />
            </IconButton>
          }
        >
          Failed to load dashboard data: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with status indicators */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Speed sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant='h4' component='h1' sx={{ flexGrow: 1 }}>
          EI Innovation Effectiveness Top Level KPI Dashboard
        </Typography>

        {/* Connection status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!connectionStatus.isOnline && (
            <Chip
              icon={<CloudOff />}
              label='Offline'
              color='warning'
              size='small'
            />
          )}

          {hasErrors && (
            <Chip
              icon={<ErrorIcon />}
              label='Errors'
              color='error'
              size='small'
              onClick={clearErrors}
            />
          )}

          {dataFreshness.isStale && (
            <Chip
              label={`Updated ${dataFreshness.minutesSinceUpdate}m ago`}
              color='default'
              size='small'
            />
          )}

          <IconButton
            onClick={() => refresh.leadership()}
            disabled={isRefreshing}
            size='small'
          >
            <Refresh />
          </IconButton>

          <Button
            variant='contained'
            size='small'
            startIcon={<Assessment />}
            onClick={() => setCollectorStatsOpen(true)}
          >
            collector stats
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <FilterPanel
          filters={draftFilters}
          onFiltersChange={handleDraftChange}
          onReset={resetFilters}
          onApply={() => applyFilters(draftFilters)}
          applyDisabled={!hasPendingChanges}
          filterConfigs={filterConfigs}
          showPresets={true}
          enableUrlSync={false}
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* EI Innovation Effectiveness Top Level KPIs Section */}
      <EIRnDTopLevelKPISection
        data={data as any}
        loading={isLoading}
        filters={filters}
      />

      <CollectorStatsDialog
        open={isCollectorStatsOpen}
        onClose={() => setCollectorStatsOpen(false)}
      />
    </Box>
  );
};
